import { useEffect, useState } from "react";
import { IdCard, MessageSquareWarning, ShieldCheck } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { api, formatApiErrorDetail } from "@/lib/api";
import { MY_DATA } from "@/constants/testIds";
import { toast } from "sonner";

const FIELDS = [
  { key: "full_name", label: "Nombre completo" },
  { key: "email", label: "Correo" },
  { key: "position", label: "Puesto" },
  { key: "department", label: "Departamento" },
  { key: "work_center", label: "Centro de trabajo" },
  { key: "hire_date", label: "Fecha de contratación" },
  { key: "phone", label: "Teléfono" },
  { key: "curp", label: "CURP" },
  { key: "rfc", label: "RFC" },
  { key: "nss", label: "NSS (Seguro Social)" },
  { key: "fecha_nacimiento", label: "Fecha de nacimiento" },
  { key: "nacionalidad", label: "Nacionalidad" },
  { key: "domicilio", label: "Domicilio" },
  { key: "beneficiarios", label: "Beneficiarios" },
  { key: "contacto_emergencia_nombre", label: "Contacto de emergencia" },
  { key: "contacto_emergencia_telefono", label: "Teléfono de emergencia" },
  { key: "contacto_emergencia_parentesco", label: "Parentesco" },
];

const REQUEST_STATUS_LABEL = { pending: "Pendiente de revisión por RH", resolved: "Resuelta" };

export function MyDataSection({ employee, employeeId, companyName = "tu empresa" }) {
  const [open, setOpen] = useState(false);
  const [message, setMessage] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [requests, setRequests] = useState([]);

  const loadRequests = () => {
    api.get(`/employees/${employeeId}/data-correction-requests`).then(({ data }) => setRequests(data));
  };

  useEffect(loadRequests, [employeeId]);

  const handleSubmit = async () => {
    if (!message.trim()) {
      toast.error("Describe qué dato necesitas corregir");
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await api.post(`/employees/${employeeId}/data-correction-requests`, { message });
      toast.success(data.message);
      if (!data.admin_notified) {
        toast.warning("No pudimos notificar a RH por correo, pero tu solicitud ya quedó registrada y visible para ellos.");
      }
      setOpen(false);
      setMessage("");
      loadRequests();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid={MY_DATA.section} className="mb-6">
      <p className="text-sm font-semibold text-charcoal mb-2 flex items-center gap-1.5">
        <IdCard className="w-4 h-4 text-petroleum" strokeWidth={1.5} /> Mis datos
      </p>
      <div className="bg-card border border-petroleum/10 rounded-2xl p-4">
        <div className="grid grid-cols-2 gap-3">
          {FIELDS.map((f) => (
            <div key={f.key}>
              <p className="text-[11px] text-charcoal/45 uppercase tracking-wide">{f.label}</p>
              <p data-testid={`${MY_DATA.fieldValuePrefix}-${f.key}`} className="text-sm text-charcoal">
                {employee[f.key] || <span className="text-charcoal/30">Sin capturar</span>}
              </p>
            </div>
          ))}
        </div>

        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button data-testid={MY_DATA.requestCorrectionButton} variant="outline" size="sm" className="mt-4 rounded-full border-terracotta/40 text-terracotta hover:bg-terracotta/5">
              <MessageSquareWarning className="w-3.5 h-3.5 mr-1.5" /> Solicitar corrección
            </Button>
          </DialogTrigger>
          <DialogContent className="bg-bone">
            <DialogHeader>
              <DialogTitle>Solicitar corrección de datos</DialogTitle>
              <DialogDescription>Describe qué dato de tu expediente necesitas corregir. Se enviará al administrador de RH.</DialogDescription>
            </DialogHeader>
            <p className="text-sm text-charcoal/60">
              Estos datos son de solo lectura para mantener el control de auditoría. Describe qué dato está
              incorrecto y RH lo corregirá desde tu expediente.
            </p>
            <Textarea
              data-testid={MY_DATA.requestCorrectionTextarea}
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder="Ej. Mi domicilio está mal escrito, debería decir..."
              className="bg-white min-h-[100px]"
            />
            <Button data-testid={MY_DATA.requestCorrectionSubmitButton} disabled={submitting} onClick={handleSubmit} className="w-full bg-terracotta hover:bg-terracotta/90 rounded-full">
              {submitting ? "Enviando..." : "Enviar solicitud"}
            </Button>
          </DialogContent>
        </Dialog>

        {requests.length > 0 && (
          <div data-testid={MY_DATA.requestsList} className="mt-3 space-y-1.5">
            {requests.map((r) => (
              <div key={r.id} className="text-xs bg-petroleum/5 rounded-lg p-2 flex justify-between gap-2">
                <span className="text-charcoal/70">{r.message}</span>
                <span className={`shrink-0 font-medium ${r.status === "resolved" ? "text-emerald-600" : "text-amber-600"}`}>
                  {REQUEST_STATUS_LABEL[r.status]}
                </span>
              </div>
            ))}
          </div>
        )}
      </div>

      <div data-testid={MY_DATA.privacyNotice} className="bg-petroleum/5 border border-petroleum/15 rounded-2xl p-4 mt-3 flex gap-2.5">
        <ShieldCheck className="w-4 h-4 text-petroleum shrink-0 mt-0.5" strokeWidth={1.5} />
        <p className="text-xs text-charcoal/60 leading-relaxed">
          <b className="text-charcoal/80">Aviso de privacidad simplificado:</b> {companyName} es responsable del
          tratamiento de tus datos personales laborales (identificación, contacto, remuneración y expediente
          disciplinario). Se usan únicamente para administrar tu relación laboral y cumplir obligaciones legales
          en materia de trabajo, seguridad social y fiscal. Puedes consultar el Aviso de Privacidad Integral
          completo en la sección "Políticas" de este portal.
        </p>
      </div>
    </div>
  );
}
